/**
 * Generates number pattern questions for Number Patterns game.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const generateDistractors = (correct, count = 3) => {
    const seen = new Set([correct]);
    const distractors = [];
    const step = Math.abs(correct) < 100 ? 5 : 20;
    const offsets = [step, step * 2, -step, -step * 2, step + 1, step - 1];
    while (distractors.length < count) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        const d = correct + off;
        if (d !== correct && !seen.has(d)) {
            seen.add(d);
            distractors.push(d);
        }
    }
    return distractors;
};

/**
 * Type A: Add a constant (e.g. 2, 4, 6, 8, ?)
 */
const addConstant = (difficulty = "normal") => {
    let minStep = 2;
    let maxStep = 6;
    if (difficulty === "easy") {
        maxStep = 4;
    } else if (difficulty === "hard") {
        maxStep = 10;
    }
    const step = Math.floor(Math.random() * (maxStep - minStep + 1)) + minStep;
    const start =
        difficulty === "hard"
            ? Math.floor(Math.random() * 50) + 20
            : Math.floor(Math.random() * 20);
    const sequence = [
        start,
        start + step,
        start + step * 2,
        start + step * 3,
        start + step * 4,
    ];
    return { sequence };
};

/**
 * Type B: Multiply by constant (e.g. 2, 4, 8, 16, ?)
 */
const multiplyConstant = (difficulty = "normal") => {
    const factors = difficulty === "easy" ? [2] : [2, 3];
    const factor = factors[Math.floor(Math.random() * factors.length)];
    const start = Math.floor(Math.random() * 5) + 1;
    const sequence = [
        start,
        start * factor,
        start * factor * factor,
        start * Math.pow(factor, 3),
        start * Math.pow(factor, 4),
    ];
    return { sequence };
};

/**
 * Type C: Subtract constant
 */
const subtractConstant = (difficulty = "normal") => {
    let minStep = 2;
    let maxStep = 4;
    if (difficulty === "hard") {
        maxStep = 8;
    }
    const step = Math.floor(Math.random() * (maxStep - minStep + 1)) + minStep;
    const start =
        difficulty === "easy"
            ? Math.floor(Math.random() * 30) + 20
            : Math.floor(Math.random() * 40) + 40;
    const sequence = [
        start,
        start - step,
        start - step * 2,
        start - step * 3,
        start - step * 4,
    ];
    return { sequence };
};

/**
 * Type D: Add increasing amount (1, 2, 4, 7, 11, ? - triangular-ish)
 */
const addIncreasing = (difficulty = "normal") => {
    const start = Math.floor(Math.random() * 10) + 1;
    let a = start;
    const sequence = [a];
    let add = 2;
    for (let i = 0; i < 3; i++) {
        a += add;
        sequence.push(a);
        add += 1;
    }
    // One more step for the 5th term
    a += add;
    sequence.push(a);
    return { sequence };
};

/**
 * Type E: Alternating + / - pattern (e.g. +2, -1, +2, -1, ...)
 */
const alternatingAddSub = (difficulty = "normal") => {
    const base = difficulty === "hard" ? Math.floor(Math.random() * 50) + 20 : Math.floor(Math.random() * 20) + 5;
    const stepA = Math.floor(Math.random() * 3) + 2; // 2-4
    const stepB = Math.floor(Math.random() * 3) + 1; // 1-3

    const sequence = [base];
    let current = base;
    const ops = ["+", "-"];

    for (let i = 0; i < 4; i++) {
        const op = ops[i % 2];
        if (op === "+") {
            current += stepA;
        } else {
            current -= stepB;
        }
        if (i < 4) {
            sequence.push(current);
        }
    }

    return { sequence };
};

/**
 * Type F: Divide by constant (e.g. 80, 40, 20, 10, ?)
 */
const divideConstant = (difficulty = "normal") => {
    const factors = difficulty === "easy" ? [2] : [2, 4, 5];
    const factor = factors[Math.floor(Math.random() * factors.length)];
    const base = Math.floor(Math.random() * 10) + 2;
    const start = base * Math.pow(factor, 4);
    const sequence = [
        start,
        start / factor,
        start / Math.pow(factor, 2),
        start / Math.pow(factor, 3),
        start / Math.pow(factor, 4),
    ];
    return { sequence };
};

/**
 * Type G: Mixed operations pattern (e.g. +3, ×2, +3, ×2, ...)
 */
const mixedOperations = (difficulty = "normal") => {
    const start = difficulty === "hard" ? Math.floor(Math.random() * 20) + 10 : Math.floor(Math.random() * 10) + 5;
    const addStep = Math.floor(Math.random() * 3) + 2; // 2-4
    const mulFactor = difficulty === "hard" ? 3 : 2;

    const sequence = [start];
    let current = start;

    for (let i = 0; i < 4; i++) {
        if (i % 2 === 0) {
            current += addStep;
        } else {
            current *= mulFactor;
        }
        sequence.push(current);
    }

    return { sequence };
};

/**
 * Generate a number pattern question.
 */
export const generatePatternQuestion = (difficulty = "normal") => {
    // Easy: only + / - constant steps
    const easyTypes = [addConstant, subtractConstant];

    // Medium: +, -, alternating +/-, and × or ÷ patterns
    const mediumTypes = [addConstant, subtractConstant, alternatingAddSub, multiplyConstant, divideConstant];

    // Hard: ×, ÷, increasing gaps, and mixed operation patterns
    const hardTypes = [multiplyConstant, divideConstant, addIncreasing, mixedOperations];

    const types =
        difficulty === "easy" ? easyTypes : difficulty === "hard" ? hardTypes : mediumTypes;
    const gen = types[Math.floor(Math.random() * types.length)];
    const { sequence } = gen(difficulty);

    // Hide a random term (not always the last) and ask for it
    const missingIndex = Math.floor(Math.random() * sequence.length);
    const answer = sequence[missingIndex];

    const displaySequence = sequence.map((value, idx) =>
        idx === missingIndex ? "?" : String(value)
    );

    const distractors = generateDistractors(answer).map(String);
    const options = shuffle([String(answer), ...distractors]);

    const seqStr = displaySequence.join(' ');

    return {
        mode: 'pattern',
        question: `Find the missing number:`,
        sequence,
        sequenceStr: seqStr,
        answer: String(answer),
        options,
        type: 'pattern',
    };
};
